import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Calendar, ArrowLeft, Plus, Trash2, Power, PowerOff,
  RefreshCw, Info, CheckCircle2, XCircle, AlertTriangle, Pencil,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PortalShell } from "@/components/PortalShell";
import { Button } from "@/components/ui/button";

/**
 * Feb 2026 — Booking Availability Control admin page.
 *
 * Route: /admin/settings/booking-availability
 *
 * Reusable — do NOT hardcode any specific rule / package here. The
 * catalogue of packages comes from the same TEST_CATALOG the backend
 * exposes via the pricing endpoint. Rules apply to package_codes
 * (e.g. "basic", "tank", "bore") so future catalog additions work
 * without a code change.
 */

// Sourced from `core/constants.TEST_CATALOG`. If we ever add a fourth
// package, add it here so it becomes selectable in this UI.
const PACKAGE_OPTIONS = [
  { code: "basic", name: "AWARE 4-Point" },
  { code: "tank",  name: "AWARE Tank 360" },
  { code: "bore",  name: "AWARE Bore 360" },
];

function fmtDate(iso) {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return `${dt.getUTCDate()} ${dt.toLocaleString("en-AU", { month: "short", timeZone: "UTC", year: "numeric" })}`;
}

function todayIso() {
  const d = new Date();
  const brisbane = new Date(d.getTime() + (10 * 60 - d.getTimezoneOffset()) * 60_000);
  return brisbane.toISOString().slice(0, 10);
}

function statusChip(rule) {
  const today = todayIso();
  if (!rule.active) return { label: "Inactive", cls: "bg-slate-100 text-slate-600 border-slate-300" };
  if (rule.start_date <= today && today <= rule.end_date) return { label: "Active now", cls: "bg-rose-100 text-rose-800 border-rose-300" };
  if (today < rule.start_date) return { label: "Upcoming", cls: "bg-amber-100 text-amber-800 border-amber-300" };
  return { label: "Ended", cls: "bg-slate-100 text-slate-500 border-slate-200" };
}


function RuleForm({ initial, onCancel, onSaved }) {
  const [form, setForm] = useState(() => ({
    name:                 initial?.name || "",
    start_date:           initial?.start_date || "",
    end_date:             initial?.end_date || "",
    available_again_date: initial?.available_again_date || "",
    package_codes:        initial?.package_codes || [],
    rule_type:            initial?.rule_type || "unavailable",
    customer_message:     initial?.customer_message || "",
    internal_note:        initial?.internal_note || "",
    active:               initial?.active === undefined ? true : !!initial.active,
  }));
  const [saving, setSaving] = useState(false);

  const isEdit = !!(initial && initial.id);

  const toggleCode = (code) => {
    setForm((prev) => {
      const codes = prev.package_codes || [];
      return {
        ...prev,
        package_codes: codes.includes(code)
          ? codes.filter((c) => c !== code)
          : [...codes, code],
      };
    });
  };

  // Auto-derive `available_again_date` = end_date + 1 as the user types
  // the end date, unless they've explicitly overridden it.
  const [aaTouched, setAaTouched] = useState(!!initial?.available_again_date);
  useEffect(() => {
    if (aaTouched || !form.end_date) return;
    const [y, m, d] = form.end_date.split("-").map(Number);
    const next = new Date(Date.UTC(y, m - 1, d + 1));
    const nextIso = next.toISOString().slice(0, 10);
    setForm((prev) => ({ ...prev, available_again_date: nextIso }));
  }, [form.end_date, aaTouched]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      const url = isEdit
        ? `/admin/booking-availability/rules/${initial.id}`
        : "/admin/booking-availability/rules";
      const method = isEdit ? "put" : "post";
      const { data } = await api[method](url, payload);
      toast.success(isEdit ? "Rule updated" : "Rule created");
      onSaved(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="rounded-2xl border border-indigo-200 bg-indigo-50/40 p-5 space-y-4"
      data-testid="booking-availability-rule-form"
    >
      <h3 className="text-base font-bold text-slate-900">
        {isEdit ? "Edit rule" : "New availability rule"}
      </h3>

      <div>
        <label className="block text-xs font-semibold text-slate-700 mb-1">
          Rule name <span className="text-rose-600">*</span>
        </label>
        <input
          type="text" value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          data-testid="booking-availability-form-name"
          placeholder="e.g. Lab tech away — July 2026"
          className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="block text-xs font-semibold text-slate-700 mb-1">
            Start date <span className="text-rose-600">*</span>
          </label>
          <input
            type="date" value={form.start_date}
            onChange={(e) => setForm({ ...form, start_date: e.target.value })}
            data-testid="booking-availability-form-start"
            className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-700 mb-1">
            End date <span className="text-rose-600">*</span>
          </label>
          <input
            type="date" value={form.end_date}
            onChange={(e) => setForm({ ...form, end_date: e.target.value })}
            data-testid="booking-availability-form-end"
            className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm"
          />
          <div className="text-[10px] text-slate-500 mt-0.5">Inclusive</div>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-700 mb-1">
            Available again
          </label>
          <input
            type="date" value={form.available_again_date}
            onChange={(e) => { setAaTouched(true); setForm({ ...form, available_again_date: e.target.value }); }}
            data-testid="booking-availability-form-available-again"
            className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm"
          />
          <div className="text-[10px] text-slate-500 mt-0.5">
            Defaults to end date + 1. Override if practical.
          </div>
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-700 mb-2">
          Affected packages <span className="text-rose-600">*</span>
        </label>
        <div className="flex flex-wrap gap-2">
          {PACKAGE_OPTIONS.map((p) => {
            const on = form.package_codes.includes(p.code);
            return (
              <button
                key={p.code} type="button"
                onClick={() => toggleCode(p.code)}
                data-testid={`booking-availability-form-code-${p.code}`}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                  on ? "bg-rose-100 border-rose-300 text-rose-800" : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50"
                }`}
              >
                {on ? "✓ " : ""}{p.name}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-700 mb-1">
          Customer-facing message
        </label>
        <textarea
          rows={3} value={form.customer_message}
          onChange={(e) => setForm({ ...form, customer_message: e.target.value })}
          data-testid="booking-availability-form-message"
          placeholder="Shown on the public booking page when this rule applies."
          className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm"
        />
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-700 mb-1">
          Internal note (admin only)
        </label>
        <textarea
          rows={2} value={form.internal_note}
          onChange={(e) => setForm({ ...form, internal_note: e.target.value })}
          data-testid="booking-availability-form-internal"
          placeholder="Context for other staff (never shown to customers)."
          className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm"
        />
      </div>

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox" checked={form.active}
          onChange={(e) => setForm({ ...form, active: e.target.checked })}
          data-testid="booking-availability-form-active"
        />
        <span className="font-semibold">Active</span>
        <span className="text-[11px] text-slate-500">— inactive rules are ignored server-side.</span>
      </label>

      <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
        <Button variant="outline" onClick={onCancel} disabled={saving} className="rounded-full">
          <XCircle className="w-4 h-4 mr-1.5" /> Cancel
        </Button>
        <Button
          onClick={save} disabled={saving}
          data-testid="booking-availability-form-save"
          className="rounded-full bg-indigo-600 hover:bg-indigo-700 text-white"
        >
          <CheckCircle2 className="w-4 h-4 mr-1.5" />
          {saving ? "Saving…" : (isEdit ? "Save changes" : "Create rule")}
        </Button>
      </div>
    </div>
  );
}


export default function AdminBookingAvailability() {
  const [rows, setRows]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing]   = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/booking-availability/rules");
      setRows(data.rows || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleActive = async (rule) => {
    try {
      const { data } = await api.post(
        `/admin/booking-availability/rules/${rule.id}/active`,
        { active: !rule.active },
      );
      toast.success(data.active ? "Rule activated" : "Rule deactivated");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (rule) => {
    if (!confirm(`Delete rule "${rule.name}" permanently?\n\nTip: you can also just deactivate it to keep the history.`)) return;
    try {
      await api.delete(`/admin/booking-availability/rules/${rule.id}`);
      toast.success("Rule deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const activeCount = useMemo(() => {
    const today = todayIso();
    return rows.filter((r) => r.active && r.start_date <= today && today <= r.end_date).length;
  }, [rows]);

  return (
    <PortalShell>
      <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="admin-booking-availability-page">
        <div className="flex items-center gap-3">
          <Link
            to="/admin"
            className="text-slate-500 hover:text-slate-800 flex items-center gap-1 text-sm"
          >
            <ArrowLeft className="w-4 h-4" /> Admin
          </Link>
        </div>

        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
            <Calendar className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Booking Availability</h1>
            <p className="text-sm text-slate-600">
              Temporarily disable one or more test packages for a date range
              — e.g. during lab-tech leave. Existing bookings are not affected.
            </p>
          </div>
        </div>

        {activeCount > 0 && (
          <div
            className="rounded-xl border border-rose-300 bg-rose-50 p-3 flex items-start gap-3"
            data-testid="admin-booking-availability-active-banner"
          >
            <AlertTriangle className="w-5 h-5 text-rose-700 mt-0.5 shrink-0" />
            <div className="text-sm text-rose-900">
              <span className="font-bold">{activeCount}</span> rule{activeCount > 1 ? "s" : ""} currently active. Customers cannot book the affected packages.
            </div>
          </div>
        )}

        <div className="flex justify-between items-center">
          <div className="text-xs text-slate-500">
            {loading ? "Loading…" : `${rows.length} rule${rows.length === 1 ? "" : "s"}`}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline" size="sm" onClick={load} disabled={loading}
              className="rounded-full"
              data-testid="admin-booking-availability-refresh-btn"
            >
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
            {!creating && !editing && (
              <Button
                onClick={() => setCreating(true)}
                data-testid="admin-booking-availability-new-btn"
                className="rounded-full bg-indigo-600 hover:bg-indigo-700 text-white" size="sm"
              >
                <Plus className="w-4 h-4 mr-1.5" /> New rule
              </Button>
            )}
          </div>
        </div>

        {creating && (
          <RuleForm
            initial={null}
            onCancel={() => setCreating(false)}
            onSaved={() => { setCreating(false); load(); }}
          />
        )}

        {editing && (
          <RuleForm
            initial={editing}
            onCancel={() => setEditing(null)}
            onSaved={() => { setEditing(null); load(); }}
          />
        )}

        {!loading && rows.length === 0 && !creating && (
          <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center">
            <Info className="w-8 h-8 text-slate-400 mx-auto mb-2" />
            <div className="text-sm text-slate-600">No availability rules yet.</div>
            <div className="text-xs text-slate-400 mt-1">Click &quot;New rule&quot; to disable one or more packages for a date range.</div>
          </div>
        )}

        {rows.length > 0 && (
          <div className="space-y-3" data-testid="admin-booking-availability-rules-list">
            {rows.map((rule) => {
              const chip = statusChip(rule);
              return (
                <div
                  key={rule.id}
                  data-testid={`admin-booking-availability-rule-${rule.id}`}
                  className="rounded-2xl border border-slate-200 bg-white p-4"
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <div className="font-bold text-slate-900 text-sm">{rule.name}</div>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${chip.cls}`}>
                          {chip.label}
                        </span>
                      </div>
                      <div className="text-[12px] text-slate-600">
                        {fmtDate(rule.start_date)} – {fmtDate(rule.end_date)} · available again {fmtDate(rule.available_again_date)}
                      </div>
                      <div className="text-[11px] text-slate-500 mt-1 flex flex-wrap gap-1">
                        {(rule.package_codes || []).map((c) => (
                          <span key={c} className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 border border-slate-200 text-slate-700 font-mono">
                            {c}
                          </span>
                        ))}
                      </div>
                      {rule.customer_message && (
                        <div className="text-[11px] text-slate-700 mt-2 rounded-lg bg-slate-50 border border-slate-200 p-2">
                          <span className="text-slate-500 font-semibold">Customer sees:</span> {rule.customer_message}
                        </div>
                      )}
                      {rule.internal_note && (
                        <div className="text-[11px] text-slate-500 italic mt-1">
                          Internal: {rule.internal_note}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <button
                        onClick={() => setEditing(rule)}
                        data-testid={`admin-booking-availability-edit-${rule.id}`}
                        className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-600" title="Edit"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => toggleActive(rule)}
                        data-testid={`admin-booking-availability-toggle-${rule.id}`}
                        className={`p-1.5 rounded-lg hover:bg-slate-100 ${rule.active ? "text-emerald-700" : "text-slate-400"}`}
                        title={rule.active ? "Deactivate" : "Activate"}
                      >
                        {rule.active ? <Power className="w-4 h-4" /> : <PowerOff className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => remove(rule)}
                        data-testid={`admin-booking-availability-delete-${rule.id}`}
                        className="p-1.5 rounded-lg hover:bg-rose-50 text-rose-600" title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </PortalShell>
  );
}
